import { z } from 'zod';

import { RequestCarrierState } from './requests/status.js';
// WHY (#1580): single source for the Russia bid-scope enum lives in
//   `russia-request-bids.ts` (that module has no dependency back on this
//   one, so the import is acyclic). Reuse it for the per-city bid wire +
//   read shapes instead of re-declaring the literal.
import { RussiaCarrierBidScopeSchema } from './russia-request-bids.js';

// WHY (#1167): the Russia carriers-tab bid no longer carries `unit` on
//   the wire — the new form is `price` + `comment` + `validityDays` only.
//   The enum is still exported because the parent /russia-rates list
//   primary-bid summary (`RussiaRequestPrimaryBidSummarySchema`) retains
//   it for the unchanged list/header column.
export const RussiaRequestCarrierBidUnitSchema = z.enum(['total', 'kg', 'm3', 'piece']);
export type RussiaRequestCarrierBidUnit = z.infer<typeof RussiaRequestCarrierBidUnitSchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequestCarrierBid` row.
 *   `expiresAt` and `isExpired` are server-computed from
 *   `quotedAt + validityDays days`.
 * @aiInvariant the serializer is the sole writer of `expiresAt` /
 *   `isExpired`; the columns are not stored.
 * @aiInvariant (#1580) `scope` discriminates a whole-request bid
 *   (`REQUEST`, `scopeRefId === null`) from a per-departure-city bid
 *   (`START_CITY`, `scopeRefId === the RussiaRequestStartCity.arrivalCityId`).
 *   `isWinner` is the server-resolved per-bid winner flag (true when this
 *   bid is the request's stored winner). Expand-only: the legacy bid shape
 *   was `REQUEST`-only; START_CITY rows now ride the same projection.
 * @aiPii `scopeRefId` is an `ArrivalCity` UUID — reference data, NOT new
 *   PII. No carrier identity is added here.
 */
export const RussiaRequestCarrierBidSchema = z
  .object({
    id: z.string().uuid(),
    price: z.number(),
    comment: z.string(),
    validityDays: z.number().int(),
    quotedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    isExpired: z.boolean(),
    scope: RussiaCarrierBidScopeSchema,
    // WHY (#1580): non-null only for START_CITY bids (the start-city
    //   arrivalCityId). REQUEST/legacy bids carry null.
    scopeRefId: z.string().uuid().nullable(),
    // WHY (#1580): server-resolved per-city / request winner marker so the
    //   carriers tab highlights the awarded bid without re-deriving from
    //   `winnerBidId` per row.
    isWinner: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  // INVARIANT (#1580): scope/scopeRefId both-or-neither — REQUEST carries a
  //   null ref, START_CITY a non-null one. Catches a projector that drifts
  //   away from the persisted row.
  .superRefine((bid, ctx) => {
    if (bid.scope === 'START_CITY' && bid.scopeRefId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'START_CITY bid requires a non-null scopeRefId',
      });
    }
    if (bid.scope === 'REQUEST' && bid.scopeRefId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'REQUEST bid must carry a null scopeRefId',
      });
    }
  });
export type RussiaRequestCarrierBid = z.infer<typeof RussiaRequestCarrierBidSchema>;

/**
 * @aiPurpose wire projection of a `RussiaRequestCarrier` row after #1167.
 *   `displayCode` is a per-request `R-N` index assigned by the serializer
 *   in `(createdAt ASC, id ASC)` order. Identity fields `nameSnapshot`
 *   and `contactsSnapshot` are PII and MUST be omitted (key absent — not
 *   null) when the viewer lacks `russiaCarriers.read`.
 * @aiSecurity manager actors must NEVER receive `nameSnapshot` or
 *   `contactsSnapshot` via this projection. The serializer enforces the
 *   strip; do not branch in each call site.
 * @aiPii `nameSnapshot` (carrier display name) and `contactsSnapshot`
 *   (free-form phone/email/etc.) are the PII surface; visibility is
 *   gated on `russiaCarriers.read`.
 */
export const RussiaRequestCarrierSchema = z
  .object({
    id: z.string().uuid(),
    displayCode: z.string().regex(/^R-\d+$/),
    nameSnapshot: z.string().min(1).max(200).optional(),
    contactsSnapshot: z.string().min(1).max(2000).optional(),
    state: z.nativeEnum(RequestCarrierState),
    rejectReason: z.string().nullable(),
    bids: z.array(RussiaRequestCarrierBidSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RussiaRequestCarrier = z.infer<typeof RussiaRequestCarrierSchema>;

/**
 * @aiPurpose response envelope for `GET /russia-requests/:id/carriers`.
 *   Ordered by `(createdAt ASC, id ASC)` so the wire response is
 *   deterministic; the `displayCode` index follows the same order.
 */
export const RussiaRequestCarriersResponseSchema = z
  .object({
    items: z.array(RussiaRequestCarrierSchema),
  })
  .strict();
export type RussiaRequestCarriersResponse = z.infer<typeof RussiaRequestCarriersResponseSchema>;

const InlineCarrierBodySchema = z
  .object({
    kind: z.literal('inline'),
    name: z.string().min(1).max(200),
    contacts: z.string().min(1).max(2000),
  })
  .strict();

const FromCatalogCarrierBodySchema = z
  .object({
    kind: z.literal('fromCatalog'),
    russiaCarrierId: z.string().uuid(),
  })
  .strict();

/**
 * @aiPurpose request body for `POST /russia-requests/:id/carriers` after
 *   #1167. Discriminated by `kind`:
 *     - `inline` — `logist_ru` enters `name` + `contacts` directly; the
 *       service creates a new `RussiaCarrier` row (active = true) and
 *       uses its id + the same fields for the snapshot on the join row.
 *     - `fromCatalog` — caller picks an existing `RussiaCarrier` by id;
 *       the service copies `name` → `nameSnapshot` and `contacts` →
 *       `contactsSnapshot` onto the join row.
 *   Both branches set the legacy `carrierId` (FK to global `Carrier`) to
 *   NULL and `russiaCarrierId` to the new/picked row.
 * @aiSecurity gated by `russiaCarriers.manage` at the controller; the
 *   service still enforces the parent visibility lock.
 */
export const AddRussiaRequestCarrierBodySchema = z.discriminatedUnion('kind', [
  InlineCarrierBodySchema,
  FromCatalogCarrierBodySchema,
]);
export type AddRussiaRequestCarrierInput = z.infer<typeof AddRussiaRequestCarrierBodySchema>;

/**
 * @aiPurpose request body for
 *   `POST /russia-requests/:id/carriers/:carrierId/bids`. The form is
 *   `price` (positive), `comment` (string, default `''`), `validityDays`
 *   (1..3650, default `7`), plus the #1580 per-city scope:
 *     - `scope` defaults to `REQUEST` (omitting it keeps the legacy
 *       whole-request bid shape valid — expand-only).
 *     - `scope = START_CITY` REQUIRES a `scopeRefId` (the start-city
 *       arrivalCityId UUID); the service re-submits the same
 *       `(carrier, scopeRefId)` tuple as an UPSERT, never a 2nd row.
 *     - `scope = REQUEST` must NOT carry a `scopeRefId`.
 * @aiInvariant strict mode rejects unknown keys; the cross-field
 *   refinement enforces the scope/scopeRefId both-or-neither rule so an
 *   ambiguous body fails wire validation with 400.
 */
export const AddRussiaRequestBidBodySchema = z
  .object({
    price: z.number().positive(),
    comment: z.string().default(''),
    validityDays: z.number().int().min(1).max(3650).default(7),
    scope: RussiaCarrierBidScopeSchema.default('REQUEST'),
    scopeRefId: z.string().uuid().nullish(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (
      body.scope === 'START_CITY' &&
      (body.scopeRefId === null || body.scopeRefId === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'scopeRefId is required for a START_CITY bid',
      });
    }
    if (body.scope === 'REQUEST' && body.scopeRefId !== null && body.scopeRefId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeRefId'],
        message: 'scopeRefId must be absent for a REQUEST bid',
      });
    }
  });
export type AddRussiaRequestBidInput = z.infer<typeof AddRussiaRequestBidBodySchema>;
// WHY (#1580): the wire-input view (pre-default) so a client may omit
//   `scope` for a legacy whole-request bid and let the server default it to
//   REQUEST — keeping the legacy POST body byte-identical (expand-only).
export type AddRussiaRequestBidInputWire = z.input<typeof AddRussiaRequestBidBodySchema>;

/**
 * @aiPurpose request body for
 *   `PATCH /russia-requests/:id/carriers/:carrierId` (#1168). The
 *   snapshot fields are PII; `manager` is gated out at the controller
 *   via `russiaCarriers.manage`. Editing snapshot fields here is local
 *   to the join row — the parent `RussiaCarrier` directory entry is
 *   NEVER touched (it has its own edit surface in #1166).
 * @aiInvariant non-empty body refinement: at least one of `name` or
 *   `contacts` must be present. An empty body MUST surface a typed
 *   validation error at the wire, not a no-op write.
 */
export const UpdateRussiaRequestCarrierBodySchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    contacts: z.string().min(1).max(2000).optional(),
  })
  .strict()
  .refine((o) => Object.keys(o).length > 0, {
    message: 'empty body: at least one of name, contacts is required',
  });
export type UpdateRussiaRequestCarrierInput = z.infer<typeof UpdateRussiaRequestCarrierBodySchema>;

/**
 * @aiPurpose request body for
 *   `PATCH /russia-requests/:id/carriers/:carrierId/bids/:bidId`
 *   (#1168). Slim post-#1167 shape. Server refreshes `quotedAt = now()`
 *   on every successful update so `expiresAt` re-derives from the moment
 *   of the edit.
 * @aiInvariant non-empty body refinement. `strict()` rejects unknown
 *   keys so a stale client that still sends `currency` / `unit` / etc.
 *   fails wire validation with 400.
 */
export const UpdateRussiaRequestBidBodySchema = z
  .object({
    price: z.number().positive().optional(),
    comment: z.string().optional(),
    validityDays: z.number().int().min(1).max(3650).optional(),
  })
  .strict()
  .refine((o) => Object.keys(o).length > 0, {
    message: 'empty body: at least one of price, comment, validityDays is required',
  });
export type UpdateRussiaRequestBidInput = z.infer<typeof UpdateRussiaRequestBidBodySchema>;
